"use client";

import { useQueries } from "@tanstack/react-query";
import type { Bet, LiveWin } from "@/lib/data";
import type { BankrollTile } from "@/lib/api/mappers";
import { useCasinoBets } from "@/lib/api/hooks";
import { erc20BalanceOf } from "@/lib/chain-balance";
import { Bankroll, LiveWinsStrip, LiveWinsTicker } from "./Feeds";
import { useContent } from "@/components/shell/ContentContext";
import { LiveStats } from "./LiveStats";

/* The feeds are labelled "live" and carry a pulsing dot, so they have to
   actually move. The server renders the first frame — that is what makes the
   lobby fast — and these wrappers keep it current from the browser.

   Polling rather than the socket: `GET /realtime` carries transaction events,
   not bet objects, so it can only ever say "something happened", never what.
   Until the API pushes bets, five seconds is the honest way to stay current. */

const REFRESH_MS = 5000;

export function LiveWinsStripLive({ initial }: { initial: LiveWin[] }) {
  const { wins } = useCasinoBets({ limit: 50, refetchMs: REFRESH_MS });

  return <LiveWinsStrip wins={wins.length > 0 ? wins : initial} />;
}

export function LiveWinsTickerLive({ initial }: { initial: LiveWin[] }) {
  const { wins } = useCasinoBets({ limit: 50, refetchMs: REFRESH_MS });

  return <LiveWinsTicker wins={wins.length > 0 ? wins : initial} />;
}

export function LiveStatsLive({ initial }: { initial: Bet[] }) {
  const { bets } = useCasinoBets({ limit: 50, refetchMs: REFRESH_MS });

  return <LiveStats bets={bets.length > 0 ? bets : initial} />;
}

/* A house bankroll tile shows the pool's real liquidity: the ERC-20 balance of
   its on-chain address, read from the chain's public RPC — the same figure the
   whitelabel reads through wagmi, and nothing to do with the signed-in player's
   own balance (which is what this used to show by mistake). The server seeds the
   figure so the first paint has it; this keeps each tile current on a 20s poll,
   and fills any the server could not reach. */
export function BankrollLive({ initial }: { initial: BankrollTile[] }) {
  const { bankroll } = useContent();

  /* An operator-configured cold wallet per token symbol; its balance is the
     same on-chain read as the pool's, at the cold address. */
  const coldAddressOf = (tile: BankrollTile) =>
    bankroll.coldWallets.find((wallet) => wallet.symbol.trim().toUpperCase() === tile.symbol && wallet.address.trim())
      ?.address.trim() ?? null;

  const reads = useQueries({
    queries: initial.map((tile) => ({
      queryKey: ["bankroll-balance", tile.id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        erc20BalanceOf({
          chainId: tile.chainId,
          tokenAddress: tile.tokenAddress,
          holder: tile.address,
          signal,
        }).then((value) => value?.toString() ?? null),
      initialData: tile.balance,
      refetchInterval: 20_000,
      staleTime: 15_000,
    })),
  });

  const coldReads = useQueries({
    queries: initial.map((tile) => {
      const holder = coldAddressOf(tile);
      return {
        queryKey: ["bankroll-cold-balance", tile.id, holder],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          holder
            ? erc20BalanceOf({
                chainId: tile.chainId,
                tokenAddress: tile.tokenAddress,
                holder,
                signal,
              }).then((value) => value?.toString() ?? null)
            : Promise.resolve(null),
        enabled: holder !== null,
        refetchInterval: 20_000,
        staleTime: 15_000,
      };
    }),
  });

  const tiles = initial.map((tile, index) => ({
    ...tile,
    balance: reads[index]?.data ?? tile.balance,
    coldAddress: coldAddressOf(tile),
    coldBalance: coldReads[index]?.data ?? null,
  }));

  return <Bankroll tiles={tiles} />;
}
